// Modal del encargado para cancelar la venta de otra sucursal que retiene al cliente.
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog, Input } from '@/kit';
import { useCancelPhoneHolder } from '../api/useWizard';
import {
    CANCEL_OTHER_STORE_REASON_MAX,
    CANCEL_OTHER_STORE_REASON_MIN,
    cancelOtherStoreFailure,
    cancelOtherStoreSuccessMessage,
    isValidCancelOtherStoreReason,
} from '../phoneHolderCancel';

/** El aviso del reembolso se queda más tiempo: hay que decírselo al cliente. */
const REFUND_TOAST_MS = 15_000;

export function CancelOtherStoreSaleDialog({
    phone,
    open,
    onClose,
    onFreed,
}: {
    phone: string;
    open: boolean;
    onClose: () => void;
    /** El teléfono quedó libre: la venta se canceló o ya no había ninguna. */
    onFreed: () => void;
}) {
    const cancel = useCancelPhoneHolder();
    const [reason, setReason] = useState('');
    const length = reason.trim().length;

    const close = (): void => {
        setReason('');
        onClose();
    };

    const submit = async (): Promise<void> => {
        if (!isValidCancelOtherStoreReason(reason)) return;
        try {
            const result = await cancel.mutateAsync({ phone, reason: reason.trim() });
            const hasRefund = (result.refundPendingCents ?? 0) > 0;
            toast.success(cancelOtherStoreSuccessMessage(result), {
                duration: hasRefund ? REFUND_TOAST_MS : undefined,
            });
            close();
            onFreed();
        } catch (error) {
            const failure = cancelOtherStoreFailure(error);
            if (failure.outcome === 'FREED') {
                toast.info(failure.message);
                close();
                onFreed();
                return;
            }
            toast.error(failure.message);
            // Con motivo inválido o reintento el modal sigue abierto y conserva el motivo.
            if (failure.outcome === 'BLOCKED') close();
        }
    };

    return (
        <ConfirmDialog
            open={open}
            onOpenChange={(next) => {
                if (!next) close();
            }}
            title="Cancelar la venta de la otra sucursal"
            description="Se cancela la venta en curso de este cliente en la otra sucursal: se descarta su crédito, se reembolsa el enganche si lo cobró y se liberan el equipo y las promociones. Así puedes hacer aquí la venta completa."
            confirmLabel="Cancelar esa venta"
            cancelLabel="Regresar"
            destructive
            loading={cancel.isPending}
            confirmDisabled={!isValidCancelOtherStoreReason(reason)}
            onConfirm={() => void submit()}
        >
            <Input
                label="Motivo"
                required
                value={reason}
                maxLength={CANCEL_OTHER_STORE_REASON_MAX}
                placeholder="Por qué se cancela la venta de la otra sucursal"
                hint={`${String(length)}/${String(CANCEL_OTHER_STORE_REASON_MAX)} · mínimo ${String(CANCEL_OTHER_STORE_REASON_MIN)} caracteres`}
                onChange={(e) => {
                    setReason(e.target.value);
                }}
            />
        </ConfirmDialog>
    );
}
